import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { StatsBand } from "@/components/StatsBand";
import { WhyUs } from "@/components/WhyUs";
import { Experiences } from "@/components/Experiences";
import { Testimonials } from "@/components/Testimonials";
import { Faq } from "@/components/Faq";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { IMAGES, CONTACT } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const AboutTeaser = () => (
    <section className="py-24 lg:py-32" data-testid="about-teaser">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
            <Reveal className="relative">
                <div className="overflow-hidden rounded-2xl">
                    <img src={IMAGES.rajasthanPalace} alt="Heritage palace in Rajasthan" className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[520px]" />
                </div>
                <div className="absolute -bottom-6 -right-4 hidden rounded-xl border border-brass/30 bg-navy px-7 py-5 shadow-xl sm:block">
                    <p className="font-serif text-2xl font-semibold text-brass">Incredible India</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-ivory/60">Crafted, end to end</p>
                </div>
            </Reveal>
            <div>
                <SectionHeading
                    eyebrow="About Vistaaryatra"
                    title="A new-age travel house with old-school care"
                    description="Vistaaryatra Pvt. Ltd. was founded on a simple belief: a journey across India should feel personal. We design bespoke domestic tours — from royal Rajasthan to Himalayan heights — pairing handpicked stays with people who answer the phone, every time."
                />
                <Reveal delay={0.15}>
                    <Link
                        to="/about"
                        data-testid="about-teaser-link"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-brass-dark"
                    >
                        Discover our story
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </div>
    </section>
);

const PackagesTeaser = () => (
    <section className="relative overflow-hidden bg-navy py-24 lg:py-28" data-testid="packages-teaser">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <Reveal>
                <p className="eyebrow">Tour Packages</p>
                <h2 className="mx-auto mt-4 max-w-2xl font-serif text-2xl font-semibold tracking-tight text-ivory sm:text-3xl lg:text-4xl">
                    Our signature collections are being crafted
                </h2>
                <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-ivory/70">
                    Curated domestic itineraries for the coming season are in final preparation.
                    In the meantime, we would love to design a private journey just for you.
                </p>
                <div className="mt-9 flex flex-wrap justify-center gap-4">
                    <Link
                        to="/packages"
                        data-testid="packages-teaser-button"
                        className="rounded-full border border-brass px-8 py-3.5 text-sm font-semibold text-brass transition-all duration-300 hover:bg-brass hover:text-navy"
                    >
                        View Packages
                    </Link>
                    <Link
                        to="/book"
                        data-testid="packages-teaser-bespoke-button"
                        className="rounded-full bg-brass px-8 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:bg-brass-dark"
                    >
                        Request a Bespoke Trip
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>
);

const CtaBand = () => (
    <section className="border-y border-brass/25 bg-brass-light/50 py-16" data-testid="cta-band">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:px-8">
            <Reveal>
                <h2 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">Tell us your travel dream</h2>
                <p className="mt-2 text-sm text-steel">A free, no-obligation quote from a real travel designer — within 24 hours.</p>
            </Reveal>
            <Reveal delay={0.1} className="flex flex-wrap justify-center gap-3">
                <Link
                    to="/book"
                    data-testid="cta-band-book-button"
                    className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-navy-800"
                >
                    Start Booking
                    <ArrowRight className="h-4 w-4 text-brass transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cta-band-whatsapp-button"
                    className="inline-flex items-center gap-2 rounded-full border border-navy/30 px-8 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:border-[#25D366] hover:text-[#128C4B]"
                >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp Us
                </a>
            </Reveal>
        </div>
    </section>
);

const Home = () => (
    <>
        <Hero />
        <Marquee />
        <AboutTeaser />
        <StatsBand />
        <WhyUs />
        <Experiences />
        <PackagesTeaser />
        <Testimonials />
        <Faq />
        <CtaBand />
    </>
);

export default Home;
